import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { PortalPage, EmptyState, Field } from "../../components/portal/PortalPage";
import { usePortal } from "../../components/portal/usePortal";
import { Buildings, Plus, PencilSimple, Trash, MapPin, Phone, ClockCounterClockwise } from "@phosphor-icons/react";
import PropertyHistory from "../../components/PropertyHistory";
import { CardListSkeleton } from "../../components/Skeletons";
import { ForClientChip, FamilyFilter, useFamilyFilter } from "../../components/portal/FamilyFilter";
import { toast } from "sonner";

const EMPTY = { name: "", address_line: "", town_city: "", county: "", postcode: "", what3words: "", property_type: "", site_contact_name: "", site_contact_phone: "", site_contact_email: "", access_notes: "" };

export default function ClientPropertiesPage() {
  const { perms } = usePortal();
  const fam = useFamilyFilter();
  const [props, setProps] = useState(null);
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const shown = (props || []).filter(fam.matches);
  const load = () => API.get("/client-hub/properties").then(({ data }) => setProps(data.properties || [])).catch(() => setProps([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setF(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setF({ ...EMPTY, ...p }); setEditing(p.id); };

  const save = async () => {
    if (!f.name.trim() || !f.address_line.trim() || !f.postcode.trim()) return toast.error("Name, address and postcode are required");
    setBusy(true);
    try {
      if (editing === "new") await API.post("/client-hub/properties", f); else await API.put(`/client-hub/properties/${editing}`, f);
      toast.success(editing === "new" ? "Property added" : "Property updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name}? Existing jobs are unaffected.`)) return;
    try { await API.delete(`/client-hub/properties/${p.id}`); toast.success("Property removed"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <PortalPage
      title="Properties"
      subtitle="Register your sites once — then pick them in one click when raising requests or emergencies."
      testid="client-properties"
      actions={<><FamilyFilter fam={fam} testid="properties-client-filter" />{perms.can_manage_properties && <Button size="sm" onClick={openNew} data-testid="add-property-btn"><Plus size={14} className="mr-1" /> Add Property</Button>}</>}
    >
      {props === null ? <CardListSkeleton rows={3} showHeader={false} /> : shown.length === 0 ? (
        <EmptyState icon={Buildings} title="No properties yet" text="Add your buildings, units or sites with access details and on-site contacts." action={perms.can_manage_properties && <Button size="sm" onClick={openNew}>Add your first property</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="properties-grid">
          {shown.map((p) => (
            <div key={p.id} className={`border border-border rounded-lg bg-card p-4 flex flex-col ${historyFor === p.id ? "sm:col-span-2 lg:col-span-3" : ""}`} data-testid={`property-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                  {p.property_type && <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{p.property_type}</p>}
                  <ForClientChip name={p.for_client} clientId={p.client_id} />
                </div>
                {perms.can_manage_properties && !p.for_client && (
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`property-edit-${p.id}`}><PencilSimple size={14} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => remove(p)} data-testid={`property-delete-${p.id}`}><Trash size={14} /></Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1"><MapPin size={13} className="shrink-0 mt-0.5" /> {[p.address_line, p.town_city, p.postcode].filter(Boolean).join(", ")}</p>
              {(p.site_contact_name || p.site_contact_phone) && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Phone size={12} /> {p.site_contact_name} {p.site_contact_phone}</p>}
              {p.access_notes && <p className="text-[11px] text-muted-foreground mt-2 italic line-clamp-2">{p.access_notes}</p>}
              <div className="mt-auto pt-3 flex items-center justify-between gap-3 text-[10px] text-muted-foreground">
                <span>{p.request_count || 0} requests · {p.job_count || 0} jobs{p.visit_count ? ` · ${p.visit_count} visits` : ""} · {p.emergency_count || 0} emergencies</span>
                <button type="button" onClick={() => setHistoryFor(h => (h === p.id ? null : p.id))} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1" data-testid={`property-history-btn-${p.id}`}>
                  <ClockCounterClockwise size={12} /> {historyFor === p.id ? "Hide history" : "History"}
                </button>
              </div>
              {historyFor === p.id && (
                <div className="border-t border-border pt-3 mt-3">
                  <PropertyHistory endpoint={`/client-hub/properties/${p.id}/history`} portal testid={`property-history-${p.id}`} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="property-dialog">
          <DialogHeader><DialogTitle>{editing === "new" ? "Add Property" : "Edit Property"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Name *"><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Head Office, Block B" data-testid="prop-name" /></Field>
              <Field label="Type"><Input value={f.property_type} onChange={(e) => set("property_type", e.target.value)} placeholder="Office / Residential / Retail" data-testid="prop-type" /></Field>
            </div>
            <Field label="Address line *"><Input value={f.address_line} onChange={(e) => set("address_line", e.target.value)} data-testid="prop-address" /></Field>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Town / City"><Input value={f.town_city} onChange={(e) => set("town_city", e.target.value)} data-testid="prop-town" /></Field>
              <Field label="County"><Input value={f.county} onChange={(e) => set("county", e.target.value)} data-testid="prop-county" /></Field>
              <Field label="Postcode *"><Input value={f.postcode} onChange={(e) => set("postcode", e.target.value.toUpperCase())} data-testid="prop-postcode" /></Field>
            </div>
            <Field label="what3words"><Input value={f.what3words} onChange={(e) => set("what3words", e.target.value)} placeholder="filled.count.soap" data-testid="prop-w3w" /></Field>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Site contact"><Input value={f.site_contact_name} onChange={(e) => set("site_contact_name", e.target.value)} data-testid="prop-contact-name" /></Field>
              <Field label="Contact phone"><Input value={f.site_contact_phone} onChange={(e) => set("site_contact_phone", e.target.value)} data-testid="prop-contact-phone" /></Field>
              <Field label="Contact email"><Input value={f.site_contact_email} onChange={(e) => set("site_contact_email", e.target.value)} data-testid="prop-contact-email" /></Field>
            </div>
            <Field label="Access notes" hint="Key safe codes, parking, alarm details — shared with the contractor attending."><Textarea rows={3} value={f.access_notes} onChange={(e) => set("access_notes", e.target.value)} data-testid="prop-access" /></Field>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="prop-save">{editing === "new" ? "Add Property" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalPage>
  );
}
